import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import styles from './ThemeTab.module.css';
import { toCssColor } from './color-helpers';
import {
  CHART_COLOR_FIELDS,
  PREVIEW_FONT_BODY_FALLBACK,
  PREVIEW_FONT_HEADING_FALLBACK,
} from './theme-constants';
import type { ThemePreviewProps, ThemeStyleToken } from './theme-types';

const PREVIEW_BUTTONS: { surface: ThemeStyleToken; label: string }[] = [
  { surface: 'primary', label: 'Primary Button' },
  { surface: 'secondary', label: 'Secondary' },
  { surface: 'destructive', label: 'Destructive' },
  { surface: 'accent', label: 'Accent' },
];

export const ThemePreview: React.FC<ThemePreviewProps> = ({ resolveToken: token, darkChrome }) => {
  const shellRegionId = React.useId();
  const backgroundRegionId = React.useId();
  const fieldRegionId = React.useId();

  return (
    <div className={darkChrome ? `${styles.previewHost} dark` : styles.previewHost}>
      <Card className={styles.preview}>
        <CardContent className="pt-6">
          <h3 className={styles.previewTitle}>Live Preview</h3>

          <div
            className={styles.previewShell}
            role="region"
            aria-labelledby={shellRegionId}
            style={{
              backgroundColor: toCssColor(token('shell')),
              borderRadius: token('radius'),
            }}>
            <p id={shellRegionId} className={styles.previewLayerLabel}>
              Shell
            </p>
            <div
              className={styles.previewSurface}
              role="region"
              aria-labelledby={backgroundRegionId}
              style={
                {
                  backgroundColor: toCssColor(token('background')),
                  color: toCssColor(token('foreground')),
                  borderColor: toCssColor(token('border')),
                  borderRadius: token('radius'),
                  fontFamily: token('font-body') || PREVIEW_FONT_BODY_FALLBACK,
                  '--preview-ring': toCssColor(token('ring')),
                } as React.CSSProperties
              }>
              <p id={backgroundRegionId} className={styles.previewLayerLabel}>
                Background
              </p>

              <h3 style={{ fontFamily: token('font-heading') || PREVIEW_FONT_HEADING_FALLBACK }}>
                Theme Preview
              </h3>

              <section
                className={styles.previewCard}
                style={{
                  backgroundColor: toCssColor(token('card')),
                  color: toCssColor(token('card-foreground')),
                  borderColor: toCssColor(token('border')),
                  borderRadius: token('radius'),
                }}>
                <h4 style={{ fontFamily: token('font-heading') || PREVIEW_FONT_HEADING_FALLBACK }}>
                  Card Title
                </h4>
                <p>This is an example card component</p>
              </section>

              <aside
                className={styles.previewPopover}
                style={{
                  backgroundColor: toCssColor(token('popover')),
                  color: toCssColor(token('popover-foreground')),
                  borderColor: toCssColor(token('border')),
                  borderRadius: token('radius'),
                }}>
                <p>Popover content</p>
              </aside>

              <div className={styles.previewButtons}>
                {PREVIEW_BUTTONS.map(({ surface, label }) => (
                  <button
                    key={surface}
                    type="button"
                    style={{
                      backgroundColor: toCssColor(token(surface)),
                      color: toCssColor(token(`${surface}-foreground` as ThemeStyleToken)),
                      borderRadius: token('radius'),
                    }}>
                    {label}
                  </button>
                ))}
              </div>

              <div
                className={styles.previewMuted}
                style={{
                  backgroundColor: toCssColor(token('muted')),
                  color: toCssColor(token('muted-foreground')),
                  borderRadius: token('radius'),
                }}>
                <p>Muted section with muted text</p>
              </div>

              <section className={styles.previewField} role="region" aria-labelledby={fieldRegionId}>
                <p id={fieldRegionId} className={styles.previewFieldLabel}>
                  Field
                </p>
                <input
                  type="text"
                  placeholder="Text input (uses field fill, not background)"
                  className={styles.previewInput}
                  style={{
                    backgroundColor: toCssColor(token('field')),
                    color: toCssColor(token('foreground')),
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: toCssColor(token('input')),
                    borderRadius: token('radius'),
                  }}
                />
              </section>

              <div className={styles.previewCharts}>
                {CHART_COLOR_FIELDS.map(field => (
                  <div
                    key={field.key}
                    style={{
                      backgroundColor: toCssColor(token(field.key)),
                      borderRadius: token('radius'),
                    }}
                    title={field.label}
                  />
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
